import Highcharts from 'highcharts/highstock';

/** Pixels kept between the outermost column data label and the plot edge. */
export const LABEL_EDGE_PADDING = 4;

/** Labels needing more than this share of the axis length are left alone. */
export const MAX_LABEL_SHARE = 0.8;

/** Label size changes below this many pixels never trigger a redraw. */
const REDRAW_TOLERANCE = 1;

type LabelElement = Highcharts.SVGElement & { alignAttr?: Highcharts.SVGAttributes };

type LabelPoint = Highcharts.Point & { dataLabel?: LabelElement; dataLabels?: LabelElement[]; isNull?: boolean };

type LabelSeries = Highcharts.Series & { dataMax?: number; hasDataLabels?: () => boolean };

type HeadroomAxis = Highcharts.Axis & {
    dataLabelHeadroom?: number;
    hasLabelHeadroom?: boolean;
    forceRedraw?: boolean;
    logarithmic?: unknown;
};

type HeadroomChart = Highcharts.Chart & { hasLoaded?: boolean; isAddingLabelHeadroom?: boolean };

const registered = new WeakSet<object>();

const labelShare = (labelPx: number, axisLength: number) => (labelPx + LABEL_EDGE_PADDING) / axisLength;

/** Smallest axis max that leaves `labelPx` between the tallest column and the plot edge. */
export const getHeadroomMax = (min: number, dataMax: number, labelPx: number, axisLength: number) => {
    const share = labelShare(labelPx, axisLength);
    if (!(axisLength > 0) || labelPx <= 0 || share >= MAX_LABEL_SHARE || dataMax <= min) {
        return undefined;
    }
    return min + (dataMax - min) / (1 - share);
};

const isLabelledColumnSeries = (series: LabelSeries) =>
    series.visible && series.is('column') && !!series.hasDataLabels?.();

const appliesToAxis = (axis: HeadroomAxis) => axis.coll === 'yAxis' && !axis.reversed && !axis.logarithmic;

/** Measures how far labels that can fit reach past their column ends and into the padded axis max edge, in pixels. */
export const measureLabelOverhang = (axis: HeadroomAxis) => {
    const inverted = !!axis.chart.inverted;
    let labelPx = 0;
    let overflowPx = 0;

    axis.series.forEach((series) => {
        if (!isLabelledColumnSeries(series as LabelSeries)) {
            return;
        }
        (series.points as LabelPoint[]).forEach((point) => {
            if (point.isNull || typeof point.plotY !== 'number') {
                return;
            }
            const labels = point.dataLabels ?? (point.dataLabel ? [point.dataLabel] : []);
            labels.forEach((label) => {
                const { x, y } = label.alignAttr ?? {};
                if (typeof x !== 'number' || typeof y !== 'number') {
                    return;
                }
                const box = label.getBBox();
                const edge = inverted ? axis.len - (x + box.width) : y;
                const overhang = (point.plotY as number) - edge;
                if (labelShare(overhang, axis.len) >= MAX_LABEL_SHARE) {
                    return;
                }
                labelPx = Math.max(labelPx, overhang);
                overflowPx = Math.max(overflowPx, LABEL_EDGE_PADDING - edge);
            });
        });
    });

    return { labelPx, overflowPx };
};

/** Raises a y axis max so the measured column labels fit inside the plot. */
function onFoundExtremes(this: HeadroomAxis) {
    this.hasLabelHeadroom = false;
    if (!appliesToAxis(this) || !this.dataLabelHeadroom || typeof this.min !== 'number') {
        return;
    }
    const dataMax = Math.max(
        ...this.series
            .filter((series) => isLabelledColumnSeries(series as LabelSeries))
            .map((series) => (series as LabelSeries).dataMax ?? -Infinity),
    );
    const headroomMax = getHeadroomMax(this.min, dataMax, this.dataLabelHeadroom, this.len);
    if (headroomMax !== undefined && (typeof this.max !== 'number' || headroomMax > this.max)) {
        this.max = headroomMax;
        this.hasLabelHeadroom = true;
    }
}

/** Remeasures labels after each render and redraws once if they overflow or the headroom can shrink. */
function onRender(this: HeadroomChart) {
    if (this.isAddingLabelHeadroom) {
        return;
    }
    let needsRedraw = false;

    (this.yAxis as HeadroomAxis[]).filter(appliesToAxis).forEach((axis) => {
        const previous = axis.dataLabelHeadroom ?? 0;
        const { labelPx, overflowPx } = measureLabelOverhang(axis);
        axis.dataLabelHeadroom = labelPx;
        const grow = overflowPx > 0 && labelPx > previous + REDRAW_TOLERANCE;
        const shrink = !!axis.hasLabelHeadroom && labelPx < previous - REDRAW_TOLERANCE;
        if (grow || shrink) {
            axis.forceRedraw = true;
            needsRedraw = true;
        }
    });

    if (needsRedraw) {
        this.isAddingLabelHeadroom = true;
        try {
            // Snap on first load so the initial layout never animates
            this.redraw(this.hasLoaded ? undefined : false);
        } finally {
            this.isAddingLabelHeadroom = false;
        }
    }
}

/** Registers the column data label headroom plugin and default label options on a Highcharts instance. */
export const registerDataLabelHeadroom = (H: typeof Highcharts) => {
    if (registered.has(H)) {
        return;
    }
    registered.add(H);

    H.setOptions({ plotOptions: { column: { dataLabels: { crop: false, overflow: 'allow' } } } });
    H.addEvent(H.Axis, 'foundExtremes', onFoundExtremes);
    H.addEvent(H.Chart, 'render', onRender);
};

registerDataLabelHeadroom(Highcharts);
